"use client";

import Image from "next/image";
import { useRef } from "react";
import { motion, useReducedMotion, useScroll, useTransform } from "motion/react";

import { MARKETING_EASE } from "@/components/marketing/reveal";

/**
 * The hero phone render. Rises and scales in on load, floats gently forever,
 * and drifts with scroll parallax over a breathing red glow. Client-only
 * (scroll + loops); the surrounding hero copy stays server-rendered.
 */
export function HeroPhone() {
  const reduce = useReducedMotion();
  const ref = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const parallax = useTransform(scrollYProgress, [0, 1], [0, -90]);

  return (
    <div ref={ref} className="relative mt-14 w-full">
      <motion.div style={reduce ? undefined : { y: parallax }}>
        <motion.div
          initial={reduce ? undefined : { opacity: 0, y: 60, scale: 0.94 }}
          animate={reduce ? undefined : { opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 1, ease: MARKETING_EASE, delay: 0.45 }}
          className="relative"
        >
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-0 -z-10 flex items-start justify-center"
          >
            <motion.div
              className="mt-12 h-[28rem] w-[80%] max-w-md rounded-full bg-red-600/20 blur-[6rem]"
              animate={reduce ? undefined : { opacity: [0.6, 1, 0.6], scale: [1, 1.06, 1] }}
              transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
            />
          </div>
          <motion.div
            animate={reduce ? undefined : { y: [0, -14, 0] }}
            transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
          >
            <Image
              src="/marketing/hero-dashboard.jpeg"
              alt="Ultrack home dashboard on a phone"
              width={1842}
              height={3072}
              priority
              sizes="(min-width: 640px) 22rem, 75vw"
              className="mx-auto h-auto w-[75%] max-w-sm drop-shadow-[0_2rem_4rem_rgba(0,0,0,0.7)]"
            />
          </motion.div>
        </motion.div>
      </motion.div>
    </div>
  );
}

/** Animated "Scroll" cue — a red glint that travels down a thin track. */
export function ScrollCue() {
  const reduce = useReducedMotion();

  return (
    <div className="mt-12 flex flex-col items-center gap-2 text-white/40">
      <span className="text-xs uppercase tracking-[0.25em]">Scroll</span>
      <span
        aria-hidden="true"
        className="relative h-8 w-px overflow-hidden bg-white/15"
      >
        <motion.span
          className="absolute inset-x-0 top-0 h-3 bg-gradient-to-b from-red-500 to-transparent"
          animate={reduce ? undefined : { y: ["-100%", "320%"] }}
          transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
        />
      </span>
    </div>
  );
}
